// Reine Auswahl-Logik für Interessenten-Zeitfenster — bewusst ohne React/GraphQL,
// damit sie isoliert testbar bleibt (Exklusivität "Ich kann zu allen Zeiten").

// Struktureller Zeitfenster-Typ. Deckt sich mit dem generierten
// InterestListTimeSlot (id, weekday, startTime/endTime nullable, sortOrder),
// hier aber entkoppelt gehalten, damit die Logik ohne Codegen kompiliert/testet.
export type InterestTimeSlotLike = {
    id: string
    weekday: string
    startTime?: string | null
    endTime?: string | null
    sortOrder?: number | null
}

// Wochentags-Reihenfolge Mo–So als Fallback, wenn sortOrder fehlt/gleich ist.
const WEEKDAY_ORDER: Record<string, number> = {
    Montag: 0,
    Dienstag: 1,
    Mittwoch: 2,
    Donnerstag: 3,
    Freitag: 4,
    Samstag: 5,
    Sonntag: 6,
}

/**
 * Sortiert Zeitfenster stabil nach sortOrder → Wochentag → Startzeit.
 * Fehlendes sortOrder landet hinter gesetzten Werten.
 */
export function sortTimeSlots<T extends InterestTimeSlotLike>(slots: ReadonlyArray<T>): Array<T> {
    return [...slots].sort((a, b) => {
        const orderA = a.sortOrder ?? Number.MAX_SAFE_INTEGER
        const orderB = b.sortOrder ?? Number.MAX_SAFE_INTEGER
        if (orderA !== orderB) return orderA - orderB

        const weekdayA = WEEKDAY_ORDER[a.weekday] ?? Number.MAX_SAFE_INTEGER
        const weekdayB = WEEKDAY_ORDER[b.weekday] ?? Number.MAX_SAFE_INTEGER
        if (weekdayA !== weekdayB) return weekdayA - weekdayB

        return (a.startTime ?? "").localeCompare(b.startTime ?? "")
    })
}

// Backend liefert die Zeit als String; je nach Quelle "HH:mm" oder "HH:mm:ss".
// Für die Anzeige immer auf "HH:mm" kürzen.
function toHHmm(time?: string | null): string | null {
    if (!time) return null
    return time.length > 5 ? time.slice(0, 5) : time
}

// Kurz-Wochentag für kompakte Zeilen ("Mo", "Di", ...).
const WEEKDAY_SHORT: Record<string, string> = {
    Montag: "Mo",
    Dienstag: "Di",
    Mittwoch: "Mi",
    Donnerstag: "Do",
    Freitag: "Fr",
    Samstag: "Sa",
    Sonntag: "So",
}

/**
 * Anzeige-Label: "Montag 14:00–14:45" bzw. nur "Montag", wenn keine Uhrzeit
 * hinterlegt ist. Ist nur eine der beiden Zeiten gesetzt, wird sie ohne
 * Bindestrich gezeigt.
 */
export function formatTimeSlotLabel(slot: InterestTimeSlotLike): string {
    const weekday = slot.weekday
    const startTime = toHHmm(slot.startTime)
    const endTime = toHHmm(slot.endTime)
    if (startTime && endTime) return `${weekday} ${startTime}–${endTime}`
    if (startTime) return `${weekday} ${startTime}`
    if (endTime) return `${weekday} ${endTime}`
    return weekday
}

/**
 * Kompaktes Label für Tabellenzeilen: "Mo 14:00–14:45". Fällt auf den vollen
 * Wochentag zurück, falls kein Kürzel bekannt ist.
 */
export function formatTimeSlotShort(slot: InterestTimeSlotLike): string {
    const weekday = WEEKDAY_SHORT[slot.weekday] ?? slot.weekday
    const startTime = toHHmm(slot.startTime)
    const endTime = toHHmm(slot.endTime)
    if (startTime && endTime) return `${weekday} ${startTime}–${endTime}`
    if (startTime) return `${weekday} ${startTime}`
    if (endTime) return `${weekday} ${endTime}`
    return weekday
}

/** Zeitfenster einer Liste als eine kompakte Zeile, sortiert und komma-getrennt. */
export function formatTimeSlotsShort(slots: ReadonlyArray<InterestTimeSlotLike>): string {
    return sortTimeSlots(slots).map(formatTimeSlotShort).join(", ")
}

/**
 * Gültige Auswahl = "alle Zeiten möglich" ODER mindestens ein Zeitfenster.
 * Für Listen OHNE Zeitfenster (hasTimeSlots=false) ist keine Auswahl nötig.
 */
export function isValidTimeSlotSelection(state: {
    allTimesPossible: boolean
    selectedTimeSlotIds: ReadonlyArray<string>
    hasTimeSlots: boolean
}): boolean {
    if (!state.hasTimeSlots) return true
    return state.allTimesPossible || state.selectedTimeSlotIds.length > 0
}

/**
 * Schaltet "Ich kann zu allen Zeiten" um. Aktivieren leert die Einzelauswahl
 * (gegenseitige Exklusivität); Deaktivieren lässt die Einzelauswahl unberührt.
 */
export function toggleAllTimes(
    checked: boolean,
    selectedTimeSlotIds: ReadonlyArray<string>,
): { allTimesPossible: boolean; selectedTimeSlotIds: Array<string> } {
    if (checked) {
        return { allTimesPossible: true, selectedTimeSlotIds: [] }
    }
    return { allTimesPossible: false, selectedTimeSlotIds: [...selectedTimeSlotIds] }
}

/**
 * Setzt die Einzelauswahl (aus der Checkbox-Gruppe). Eine nicht-leere Auswahl
 * deaktiviert automatisch "alle Zeiten" — beides gleichzeitig ist unzulässig.
 */
export function setSelectedTimeSlots(
    nextSelectedIds: ReadonlyArray<string>,
): { allTimesPossible: boolean; selectedTimeSlotIds: Array<string> } {
    return {
        allTimesPossible: false,
        selectedTimeSlotIds: [...nextSelectedIds],
    }
}
